'use client'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { KanaCell, KanaScript, extendedKanaGroupIds, hiraganaTable, katakanaTable, vowels } from '../kana-data'
import KanaTableSection from '../../_components/KanaTableSection'
import { getEndingVowel, getKanaAudioPath, isSameKana, type SelectedKana } from '../../_lib/kana-practice'
import { Button } from '~/components/ui/button'
import { Checkbox } from '~/components/ui/checkbox'
import { Input } from '~/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '~/components/ui/select'
import { cn } from '~/lib/utils'

type ScriptMode = 'hiragana' | 'katakana' | 'both'
type KanaPoolMode = 'base' | 'yoon' | 'both'
type RomajiRevealMode = 'timed' | 'manual'
type AudioPlaybackMode = 'auto' | 'manual'

type RandomSettings = {
    scriptMode: ScriptMode
    kanaPoolMode: KanaPoolMode
    selectedVowels: string[]
    selectedExtendedGroupIds: string[]
    speedMs: number
    romajiRevealMode: RomajiRevealMode
    audioPlaybackMode: AudioPlaybackMode
    autoKanaOnlyPercent: number
    manualRevealDelayMs: number
    showKanaTable: boolean
}

const speedOptions = [
    { label: '1 giây', value: 1000 },
    { label: '2 giây', value: 2000 },
    { label: '3 giây', value: 3000 },
    { label: '5 giây', value: 5000 }
]
const extendedGroupOptions = katakanaTable.extendedRows.map((group) => ({
    id: group.id,
    label: group.label
}))

const settingsStorageKey = 'kana-random-settings-v1'
const defaultSettings: RandomSettings = {
    scriptMode: 'both',
    kanaPoolMode: 'base',
    selectedVowels: [...vowels],
    selectedExtendedGroupIds: [...extendedKanaGroupIds],
    speedMs: 2000,
    romajiRevealMode: 'timed',
    audioPlaybackMode: 'manual',
    autoKanaOnlyPercent: 70,
    manualRevealDelayMs: 1000,
    showKanaTable: true
}

function clampNumber(value: number, min: number, max: number) {
    return Math.min(max, Math.max(min, value))
}

export default function KanaRandomClient() {
    const [settings, setSettings] = useState<RandomSettings>(defaultSettings)
    const [autoRandom, setAutoRandom] = useState(false)
    const [showRomaji, setShowRomaji] = useState(false)
    const [selected, setSelected] = useState<SelectedKana | null>(null)
    const [randomCount, setRandomCount] = useState(0)
    const [isSettingsReady, setIsSettingsReady] = useState(false)
    const [isAudioPlaying, setIsAudioPlaying] = useState(false)
    const [audioError, setAudioError] = useState('')
    const revealTimerRef = useRef<number | null>(null)
    const audioRef = useRef<HTMLAudioElement | null>(null)
    const {
        scriptMode,
        kanaPoolMode,
        selectedVowels,
        selectedExtendedGroupIds,
        speedMs,
        romajiRevealMode,
        audioPlaybackMode,
        autoKanaOnlyPercent,
        manualRevealDelayMs,
        showKanaTable
    } = settings

    const audioPath = useMemo(() => {
        if (!selected) {
            return null
        }
        return getKanaAudioPath(selected)
    }, [selected])

    const activeTables = useMemo(() => {
        if (scriptMode === 'hiragana') {
            return [hiraganaTable]
        }
        if (scriptMode === 'katakana') {
            return [katakanaTable]
        }
        return [hiraganaTable, katakanaTable]
    }, [scriptMode])

    const randomPool = useMemo(() => {
        const collected: SelectedKana[] = []
        const includeBase = kanaPoolMode === 'base' || kanaPoolMode === 'both'
        const includeYoon = kanaPoolMode === 'yoon' || kanaPoolMode === 'both'

        for (const table of activeTables) {
            if (includeYoon) {
                for (const row of table.yoonRows) {
                    for (const cell of row) {
                        if (cell.kana) {
                            collected.push({ cell, script: table.title })
                        }
                    }
                }
            }

            if (includeBase) {
                for (const row of table.baseRows) {
                    for (const cell of row) {
                        if (!cell.kana) {
                            continue
                        }

                        const endingVowel = getEndingVowel(cell.romaji)
                        if (!endingVowel) {
                            continue
                        }

                        if (selectedVowels.includes(endingVowel)) {
                            collected.push({ cell, script: table.title })
                        }
                    }
                }
            }

            for (const extendedRow of table.extendedRows) {
                if (!selectedExtendedGroupIds.includes(extendedRow.id)) {
                    continue
                }

                for (const cell of extendedRow.cells) {
                    if (cell.kana) {
                        collected.push({ cell, script: table.title })
                    }
                }
            }
        }

        return collected
    }, [activeTables, kanaPoolMode, selectedExtendedGroupIds, selectedVowels])

    const pickRandomKana = useCallback((pool: SelectedKana[], current: SelectedKana | null) => {
        if (pool.length === 0) {
            return null
        }

        if (pool.length === 1) {
            return pool[0]
        }

        const maxRetry = 8
        let next = pool[Math.floor(Math.random() * pool.length)]
        let tries = 0

        while (isSameKana(next, current) && tries < maxRetry) {
            next = pool[Math.floor(Math.random() * pool.length)]
            tries += 1
        }

        return next
    }, [])

    const clearRevealTimer = useCallback(() => {
        if (revealTimerRef.current) {
            window.clearTimeout(revealTimerRef.current)
            revealTimerRef.current = null
        }
    }, [])

    const stopAudioPlayback = useCallback(() => {
        if (audioRef.current) {
            audioRef.current.pause()
            audioRef.current.currentTime = 0
        }
        setIsAudioPlaying(false)
    }, [])

    const scheduleReveal = useCallback(
        (mode: 'auto' | 'manual') => {
            clearRevealTimer()
            setShowRomaji(false)

            if (romajiRevealMode === 'manual') {
                return
            }

            let revealDelay = manualRevealDelayMs
            if (mode === 'auto') {
                const normalizedPercent = Math.min(95, Math.max(5, autoKanaOnlyPercent))
                revealDelay = Math.max(0, Math.round((speedMs * normalizedPercent) / 100))
            }

            revealTimerRef.current = window.setTimeout(() => {
                setShowRomaji(true)
                revealTimerRef.current = null
            }, revealDelay)
        },
        [autoKanaOnlyPercent, clearRevealTimer, manualRevealDelayMs, romajiRevealMode, speedMs]
    )

    const playAudioForKana = useCallback(
        (target: SelectedKana, forceRestart = false) => {
            const targetAudioPath = getKanaAudioPath(target)
            if (!targetAudioPath || (isAudioPlaying && !forceRestart)) {
                return
            }

            const audio = audioRef.current ?? new Audio()
            if (forceRestart) {
                audio.pause()
            }

            audio.src = targetAudioPath
            audio.currentTime = 0
            audio.onended = () => setIsAudioPlaying(false)
            audio.onerror = () => {
                setIsAudioPlaying(false)
            }
            audioRef.current = audio

            setAudioError('')
            setIsAudioPlaying(true)
            audio.play().catch(() => {
                setIsAudioPlaying(false)
                setAudioError('Không thể phát audio. Trình duyệt đã chặn hoặc file bị lỗi.')
            })
        },
        [isAudioPlaying]
    )

    const handleRandom = useCallback((mode: 'auto' | 'manual') => {
        if (randomPool.length === 0) {
            return
        }

        const nextSelected = pickRandomKana(randomPool, selected)
        if (!nextSelected) {
            return
        }

        stopAudioPlayback()
        setSelected(nextSelected)
        setRandomCount((current) => current + 1)
        scheduleReveal(mode)
        if (audioPlaybackMode === 'auto') {
            playAudioForKana(nextSelected, true)
        }
    }, [audioPlaybackMode, pickRandomKana, playAudioForKana, randomPool, scheduleReveal, selected, stopAudioPlayback])

    const handlePlayAudio = useCallback(() => {
        if (!selected || !audioPath || isAudioPlaying) {
            return
        }
        playAudioForKana(selected)
    }, [audioPath, isAudioPlaying, playAudioForKana, selected])

    useEffect(() => {
        if (!selected) {
            return
        }

        const inPool = randomPool.some((item) => isSameKana(item, selected))
        if (!inPool) {
            stopAudioPlayback()
            setSelected(null)
        }
    }, [randomPool, selected, stopAudioPlayback])

    useEffect(() => {
        if (!autoRandom || randomPool.length === 0) {
            return
        }

        const timer = window.setInterval(() => {
            handleRandom('auto')
        }, speedMs)

        return () => window.clearInterval(timer)
    }, [autoRandom, handleRandom, randomPool.length, speedMs])

    useEffect(() => {
        if (randomPool.length === 0 && autoRandom) {
            setAutoRandom(false)
        }
    }, [autoRandom, randomPool.length])

    useEffect(() => {
        const rawSettings = window.localStorage.getItem(settingsStorageKey)

        if (!rawSettings) {
            setIsSettingsReady(true)
            return
        }

        try {
            const parsedSettings = JSON.parse(rawSettings) as Partial<RandomSettings>
            const nextSettings: RandomSettings = { ...defaultSettings }

            if (parsedSettings.scriptMode && ['hiragana', 'katakana', 'both'].includes(parsedSettings.scriptMode)) {
                nextSettings.scriptMode = parsedSettings.scriptMode
            }

            if (parsedSettings.kanaPoolMode && ['base', 'yoon', 'both'].includes(parsedSettings.kanaPoolMode)) {
                nextSettings.kanaPoolMode = parsedSettings.kanaPoolMode
            }

            if (Array.isArray(parsedSettings.selectedVowels)) {
                const nextVowels = parsedSettings.selectedVowels.filter(
                    (item): item is string => typeof item === 'string' && vowels.includes(item)
                )
                nextSettings.selectedVowels = Array.from(new Set(nextVowels))
            }

            if (Array.isArray(parsedSettings.selectedExtendedGroupIds)) {
                const nextExtendedGroups = parsedSettings.selectedExtendedGroupIds.filter(
                    (item): item is string =>
                        typeof item === 'string' &&
                        extendedKanaGroupIds.includes(item as (typeof extendedKanaGroupIds)[number])
                )
                nextSettings.selectedExtendedGroupIds = Array.from(new Set(nextExtendedGroups))
            }

            if (typeof parsedSettings.speedMs === 'number' && speedOptions.some((option) => option.value === parsedSettings.speedMs)) {
                nextSettings.speedMs = parsedSettings.speedMs
            }

            if (parsedSettings.romajiRevealMode && ['timed', 'manual'].includes(parsedSettings.romajiRevealMode)) {
                nextSettings.romajiRevealMode = parsedSettings.romajiRevealMode
            }

            if (parsedSettings.audioPlaybackMode && ['auto', 'manual'].includes(parsedSettings.audioPlaybackMode)) {
                nextSettings.audioPlaybackMode = parsedSettings.audioPlaybackMode
            }

            if (typeof parsedSettings.autoKanaOnlyPercent === 'number') {
                nextSettings.autoKanaOnlyPercent = clampNumber(parsedSettings.autoKanaOnlyPercent, 5, 95)
            }

            if (typeof parsedSettings.manualRevealDelayMs === 'number') {
                nextSettings.manualRevealDelayMs = clampNumber(parsedSettings.manualRevealDelayMs, 0, 10000)
            }

            if (typeof parsedSettings.showKanaTable === 'boolean') {
                nextSettings.showKanaTable = parsedSettings.showKanaTable
            }

            setSettings(nextSettings)
        } catch {
            // Ignore invalid data in localStorage.
        } finally {
            setIsSettingsReady(true)
        }
    }, [])

    useEffect(() => {
        if (!isSettingsReady) {
            return
        }

        const settingsToSave: RandomSettings = {
            ...settings,
            autoKanaOnlyPercent: clampNumber(settings.autoKanaOnlyPercent, 5, 95),
            manualRevealDelayMs: clampNumber(settings.manualRevealDelayMs, 0, 10000)
        }

        window.localStorage.setItem(settingsStorageKey, JSON.stringify(settingsToSave))
    }, [isSettingsReady, settings])

    const toggleVowel = (vowel: string) => {
        setSettings((current) => {
            if (current.selectedVowels.includes(vowel)) {
                return {
                    ...current,
                    selectedVowels: current.selectedVowels.filter((item) => item !== vowel)
                }
            }

            return {
                ...current,
                selectedVowels: [...current.selectedVowels, vowel]
            }
        })
    }

    const toggleExtendedGroup = (groupId: string) => {
        setSettings((current) => {
            if (current.selectedExtendedGroupIds.includes(groupId)) {
                return {
                    ...current,
                    selectedExtendedGroupIds: current.selectedExtendedGroupIds.filter((item) => item !== groupId)
                }
            }

            return {
                ...current,
                selectedExtendedGroupIds: [...current.selectedExtendedGroupIds, groupId]
            }
        })
    }

    useEffect(() => {
        return () => {
            clearRevealTimer()
        }
    }, [clearRevealTimer])

    useEffect(() => {
        return () => {
            if (audioRef.current) {
                audioRef.current.pause()
                audioRef.current.onended = null
                audioRef.current.onerror = null
                audioRef.current = null
            }
        }
    }, [])

    useEffect(() => {
        if (romajiRevealMode === 'manual') {
            clearRevealTimer()
        }
    }, [clearRevealTimer, romajiRevealMode])

    useEffect(() => {
        setAudioError('')
    }, [selected?.cell.kana, selected?.script])

    const panelClass = 'mb-6 rounded-xl border border-border bg-card p-4 shadow-sm sm:p-6'
    const subPanelClass = 'rounded-lg border border-border bg-muted/40 p-3'
    const titleClass = 'text-sm font-semibold text-foreground'
    const subtleTextClass = 'text-sm text-muted-foreground'
    const dangerTextClass = 'text-sm text-destructive'

    return (
        <main className='mx-auto w-full max-w-(--breakpoint-xl) px-4 py-6 sm:px-6 sm:py-8'>
            <div className='mb-6 text-center sm:mb-8'>
                <h1 className='text-2xl font-bold text-foreground sm:text-3xl'>Luyện Kana ngẫu nhiên</h1>
                <p className='mt-2 text-sm text-muted-foreground sm:text-base'>
                    Luyện nhớ Hiragana/Katakana bằng random theo bảng chữ, nhóm âm và tốc độ bạn muốn.
                </p>
            </div>

            <section className={panelClass}>
                <h2 className='text-lg font-semibold text-foreground'>Cài đặt</h2>

                <div className='mt-4 grid gap-4 lg:grid-cols-2'>
                    <div className={subPanelClass}>
                        <p className={titleClass}>Bảng chữ</p>
                        <div className='mt-2 flex flex-wrap gap-2'>
                            {[
                                { label: 'Hiragana', value: 'hiragana' as const },
                                { label: 'Katakana', value: 'katakana' as const },
                                { label: 'Cả 2 bảng', value: 'both' as const }
                            ].map((option) => (
                                <Button
                                    key={option.value}
                                    type='button'
                                    onClick={() =>
                                        setSettings((current) => ({
                                            ...current,
                                            scriptMode: option.value
                                        }))
                                    }
                                    className={cn(
                                        'rounded-md border px-3 py-1.5 text-sm font-medium transition-colors',
                                        scriptMode === option.value
                                            ? 'border-primary bg-primary text-primary-foreground hover:bg-primary/90'
                                            : 'border-border bg-card text-foreground hover:bg-accent hover:text-accent-foreground'
                                    )}
                                >
                                    {option.label}
                                </Button>
                            ))}
                        </div>
                    </div>

                    <div className={subPanelClass}>
                        <p className={titleClass}>Nguồn random</p>
                        <div className='mt-2 flex flex-wrap gap-2'>
                            {[
                                { label: 'Theo a/i/u/e/o', value: 'base' as const },
                                { label: 'Từ ghép (yoon)', value: 'yoon' as const },
                                { label: 'Cả 2', value: 'both' as const }
                            ].map((option) => (
                                <Button
                                    key={option.value}
                                    type='button'
                                    onClick={() =>
                                        setSettings((current) => ({
                                            ...current,
                                            kanaPoolMode: option.value
                                        }))
                                    }
                                    className={cn(
                                        'rounded-md border px-3 py-1.5 text-sm font-medium transition-colors',
                                        kanaPoolMode === option.value
                                            ? 'border-primary bg-primary text-primary-foreground hover:bg-primary/90'
                                            : 'border-border bg-card text-foreground hover:bg-accent hover:text-accent-foreground'
                                    )}
                                >
                                    {option.label}
                                </Button>
                            ))}
                        </div>
                    </div>
                </div>

                {kanaPoolMode !== 'yoon' && (
                    <div className={`mt-4 ${subPanelClass}`}>
                        <p className={titleClass}>Lọc theo nguyên âm</p>
                        <div className='mt-2 flex flex-wrap gap-2'>
                            {vowels.map((vowel) => (
                                <Button
                                    key={vowel}
                                    type='button'
                                    onClick={() => toggleVowel(vowel)}
                                    className={cn(
                                        'rounded-md border px-3 py-1.5 text-sm font-semibold uppercase transition-colors',
                                        selectedVowels.includes(vowel)
                                            ? 'border-primary bg-primary text-primary-foreground hover:bg-primary/90'
                                            : 'border-border bg-card text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                                    )}
                                >
                                    {vowel}
                                </Button>
                            ))}
                        </div>
                        <div className='mt-2 flex gap-2'>
                            <Button
                                type='button'
                                onClick={() =>
                                    setSettings((current) => ({
                                        ...current,
                                        selectedVowels: [...vowels]
                                    }))
                                }
                                variant='outline'
                                size='sm'
                                className='h-7 px-2 text-xs'
                            >
                                Chọn hết
                            </Button>
                            <Button
                                type='button'
                                onClick={() =>
                                    setSettings((current) => ({
                                        ...current,
                                        selectedVowels: []
                                    }))
                                }
                                variant='outline'
                                size='sm'
                                className='h-7 px-2 text-xs'
                            >
                                Bỏ hết
                            </Button>
                        </div>
                    </div>
                )}

                <div className={`mt-4 ${subPanelClass}`}>
                    <p className={titleClass}>Nhóm phiên âm mở rộng</p>
                    <div className='mt-2 space-y-2'>
                        {extendedGroupOptions.map((group) => (
                            <label
                                key={group.id}
                                className='flex cursor-pointer items-start gap-2 rounded-md border border-transparent px-1 py-0.5 text-sm text-foreground hover:border-border hover:bg-accent'
                            >
                                <Checkbox
                                    checked={selectedExtendedGroupIds.includes(group.id)}
                                    onCheckedChange={() => toggleExtendedGroup(group.id)}
                                    className='mt-0.5 border-border data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground'
                                />
                                <span>{group.label}</span>
                            </label>
                        ))}
                    </div>
                    <div className='mt-2 flex gap-2'>
                        <Button
                            type='button'
                            onClick={() =>
                                setSettings((current) => ({
                                    ...current,
                                    selectedExtendedGroupIds: [...extendedKanaGroupIds]
                                }))
                            }
                            variant='outline'
                            size='sm'
                            className='h-7 px-2 text-xs'
                        >
                            Chọn hết
                        </Button>
                        <Button
                            type='button'
                            onClick={() =>
                                setSettings((current) => ({
                                    ...current,
                                    selectedExtendedGroupIds: []
                                }))
                            }
                            variant='outline'
                            size='sm'
                            className='h-7 px-2 text-xs'
                        >
                            Bỏ hết
                        </Button>
                    </div>
                </div>

                <div className='mt-4 grid gap-4 md:grid-cols-[1fr_auto_auto] md:items-end'>
                    <label className='block'>
                        <span className={titleClass}>Tốc độ random tự động</span>
                        <Select
                            value={String(speedMs)}
                            onValueChange={(value) =>
                                setSettings((current) => ({
                                    ...current,
                                    speedMs: Number(value)
                                }))
                            }
                        >
                            <SelectTrigger className='mt-2 w-full'>
                                <SelectValue placeholder='Chọn tốc độ random' />
                            </SelectTrigger>
                            <SelectContent>
                                {speedOptions.map((option) => (
                                    <SelectItem key={option.value} value={String(option.value)}>
                                        {option.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </label>

                    <div className='block'>
                        <span className={titleClass}>Chế độ hiển thị romaji</span>
                        <div className='mt-2 flex flex-wrap gap-2'>
                            {[
                                { label: 'Theo thời gian', value: 'timed' as const },
                                { label: 'Thủ công', value: 'manual' as const }
                            ].map((option) => (
                                <Button
                                    key={option.value}
                                    type='button'
                                    onClick={() =>
                                        setSettings((current) => ({
                                            ...current,
                                            romajiRevealMode: option.value
                                        }))
                                    }
                                    className={cn(
                                        'rounded-md border px-3 py-1.5 text-sm font-medium transition-colors',
                                        romajiRevealMode === option.value
                                            ? 'border-primary bg-primary text-primary-foreground hover:bg-primary/90'
                                            : 'border-border bg-card text-foreground hover:bg-accent hover:text-accent-foreground'
                                    )}
                                >
                                    {option.label}
                                </Button>
                            ))}
                        </div>
                    </div>

                    <div className='block'>
                        <span className={titleClass}>Chế độ phát âm</span>
                        <div className='mt-2 flex flex-wrap gap-2'>
                            {[
                                { label: 'Tự động khi random', value: 'auto' as const },
                                { label: 'Thủ công', value: 'manual' as const }
                            ].map((option) => (
                                <Button
                                    key={option.value}
                                    type='button'
                                    onClick={() =>
                                        setSettings((current) => ({
                                            ...current,
                                            audioPlaybackMode: option.value
                                        }))
                                    }
                                    className={cn(
                                        'rounded-md border px-3 py-1.5 text-sm font-medium transition-colors',
                                        audioPlaybackMode === option.value
                                            ? 'border-primary bg-primary text-primary-foreground hover:bg-primary/90'
                                            : 'border-border bg-card text-foreground hover:bg-accent hover:text-accent-foreground'
                                    )}
                                >
                                    {option.label}
                                </Button>
                            ))}
                        </div>
                    </div>

                    {romajiRevealMode === 'timed' && (
                        <label className='block'>
                            <span className='text-sm font-semibold text-foreground'>Tự động: % thời gian chỉ hiện kana</span>
                            <Input
                                type='number'
                                min={5}
                                max={95}
                                value={autoKanaOnlyPercent}
                                onChange={(event) =>
                                    setSettings((current) => ({
                                        ...current,
                                        autoKanaOnlyPercent: Number(event.target.value) || 70
                                    }))
                                }
                                className='mt-2'
                            />
                        </label>
                    )}

                    {romajiRevealMode === 'timed' && (
                        <label className='block'>
                            <span className='text-sm font-semibold text-foreground'>Thủ công: độ trễ hiện romaji (ms)</span>
                            <Input
                                type='number'
                                min={0}
                                max={10000}
                                step={100}
                                value={manualRevealDelayMs}
                                onChange={(event) =>
                                    setSettings((current) => ({
                                        ...current,
                                        manualRevealDelayMs: Number(event.target.value) || 0
                                    }))
                                }
                                className='mt-2'
                            />
                        </label>
                    )}

                    <Button
                        type='button'
                        variant={autoRandom ? 'destructive' : 'default'}
                        onClick={() => setAutoRandom((current) => !current)}
                        disabled={randomPool.length === 0}
                        className='h-10 px-4 text-sm font-semibold'
                    >
                        {autoRandom ? 'Dừng ngẫu nhiên tự động' : 'Bật ngẫu nhiên tự động'}
                    </Button>

                    <Button
                        type='button'
                        variant='default'
                        onClick={() => handleRandom('manual')}
                        disabled={randomPool.length === 0}
                        className='h-10 px-4 text-sm font-semibold'
                    >
                        Random ngay
                    </Button>

                    <Button
                        type='button'
                        variant='secondary'
                        onClick={handlePlayAudio}
                        disabled={isAudioPlaying || !audioPath}
                        className='h-10 px-4 text-sm font-semibold'
                    >
                        {isAudioPlaying ? 'Đang phát, chờ hết...' : 'Phát âm ngay'}
                    </Button>

                    {romajiRevealMode === 'manual' && (
                        <Button
                            type='button'
                            variant='outline'
                            onClick={() => setShowRomaji(true)}
                            disabled={!selected || showRomaji}
                            className='h-10 px-4 text-sm font-semibold'
                        >
                            Xem romaji
                        </Button>
                    )}
                </div>
                {audioError && <p className='mt-2 text-xs text-destructive'>{audioError}</p>}

                <div className={`mt-4 ${subPanelClass}`}>
                    <p className={titleClass}>Hiển thị bảng chữ cái bên dưới</p>
                    <Button
                        type='button'
                        onClick={() =>
                            setSettings((current) => ({
                                ...current,
                                showKanaTable: !current.showKanaTable
                            }))
                        }
                        className={cn(
                            'mt-2 rounded-md border px-3 py-1.5 text-sm font-medium transition-colors',
                            showKanaTable
                                ? 'border-primary bg-primary text-primary-foreground hover:bg-primary/90'
                                : 'border-border bg-card text-foreground hover:bg-accent hover:text-accent-foreground'
                        )}
                    >
                        {showKanaTable ? 'Đang hiển thị' : 'Đang ẩn'}
                    </Button>
                </div>

                <div className={`mt-4 flex flex-wrap gap-3 ${subtleTextClass}`}>
                    <p>
                        Tổng ký tự trong pool: <span className='font-semibold text-foreground'>{randomPool.length}</span>
                    </p>
                    <p>
                        Số lần random: <span className='font-semibold text-foreground'>{randomCount}</span>
                    </p>
                </div>

                {randomPool.length === 0 && (
                    <p className={`mt-2 ${dangerTextClass}`}>
                        Không có ký tự nào phù hợp với filter hiện tại. Hãy bật thêm bảng hoặc chọn lại nguyên âm.
                    </p>
                )}
            </section>

            <section className={panelClass}>
                <p className='text-sm uppercase tracking-widest text-muted-foreground'>Ký tự hiện tại</p>
                {selected ? (
                    <div className='mt-3'>
                        <p className='text-xs font-semibold uppercase tracking-[0.16em] text-foreground'>
                            {selected.script}
                        </p>
                        <p className='mt-2 text-7xl leading-none font-bold text-foreground sm:text-8xl'>
                            {selected.cell.kana}
                        </p>
                        {showRomaji ? (
                            <p className='mt-2 text-2xl font-semibold text-foreground'>{selected.cell.romaji}</p>
                        ) : (
                            <div className='mt-2 space-y-2'>
                                <p className='text-sm font-medium text-muted-foreground'>Romaji đang ẩn</p>
                            </div>
                        )}
                    </div>
                ) : (
                    <p className='mt-3 text-base text-muted-foreground'>
                        Chưa có ký tự. Bấm <span className='font-semibold'>Random ngay</span> để bắt đầu.
                    </p>
                )}
            </section>

            {showKanaTable && (
                <div className='space-y-6'>
                    {activeTables.map((table) => (
                        <KanaTableSection
                            key={table.title}
                            table={table}
                            selected={selected}
                            onSelect={(cell, script) => {
                                clearRevealTimer()
                                stopAudioPlayback()
                                setSelected({ cell, script })
                                setShowRomaji(true)
                            }}
                        />
                    ))}
                </div>
            )}
        </main>
    )
}
